import {
  SubscriptionsFilterParams,
  SubscriptionsService,
  SubscriptionDateAxis,
} from '@/services/subscriptions.service';
import useCurrencyStore from '@/store/useCurrencyStore';
import i18n from '@/translations';
import { hasActiveFiltersBeyondDates } from '@/utils/convertFilters';
import { formatDateForAPI, getDefaultDate, isValidDate } from '@/utils/dateHelpers';
import { useQuery } from '@tanstack/react-query';
import { differenceInDays, format, isSameDay, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsIsoDateTime,
  parseAsString,
  useQueryState,
} from 'nuqs';
import { useCallback, useMemo } from 'react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import {
  convertToVisualParams,
  normalizeParams,
  type FilterParams,
} from '@/hooks/useSales';

export type { SubscriptionDateAxis } from '@/services/subscriptions.service';

export const SUBSCRIPTION_STATUSES = [
  'pending',
  'active',
  'past_due',
  'suspended',
  'canceled',
  'expired',
] as const;

export const SUBSCRIPTION_CHART_KEYS = [
  'active_count',
  'recurring_revenue',
] as const;

export const SUBSCRIPTION_SERIES_CONFIG = {
  active_count: { color: '#A9D342', gradientId: 'gradient-active-count', yAxisId: 'count' },
  recurring_revenue: { color: '#60A5FA', gradientId: 'gradient-recurring-revenue', yAxisId: 'revenue' },
} as const;

const DEFAULT_STATUS: string[] = [...SUBSCRIPTION_STATUSES];

const getLocale = () => (i18n.language === 'pt' ? ptBR : undefined);

export const convertToSubscriptionFilterParams = (
  params: FilterParams,
): SubscriptionsFilterParams => {
  const result: SubscriptionsFilterParams = {};

  Object.entries(params).forEach(([key, value]) => {
    if (['from', 'to', 'q'].includes(key)) return;

    const values = Array.isArray(value) ? value : [value];
    const filtered = values
      .map((v) =>
        typeof v === 'string' && v.includes(':::') ? v.split(':::')[1] : v,
      )
      .filter((v) => v !== '');

    if (filtered.length > 0) {
      (result as Record<string, string[]>)[key] = filtered as string[];
    }
  });

  return result;
};

export function useSubscriptions() {
  const { t } = useTranslation(['signatures', 'sales', 'common']);

  const [currentPage, setCurrentPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(1),
  );
  const [searchQuery, setSearchQuery] = useQueryState('q', parseAsString);
  const [fromDate, setFromDate] = useQueryState(
    'from',
    parseAsIsoDateTime.withDefault(getDefaultDate()),
  );
  const [toDate, setToDate] = useQueryState(
    'to',
    parseAsIsoDateTime.withDefault(getDefaultDate()),
  );
  const [dateAxis, setDateAxis] = useQueryState(
    'date_axis',
    parseAsString.withDefault('started'),
  );
  const [statusFilter, setStatusFilter] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString),
  );
  const [paymentMethodsFilter, setPaymentMethodsFilter] = useQueryState(
    'payment_methods',
    parseAsArrayOf(parseAsString),
  );
  const [srcFilter, setSrcFilter] = useQueryState('src', parseAsString);
  const [utmCampaignFilter, setUtmCampaignFilter] = useQueryState(
    'utm_campaign',
    parseAsString,
  );
  const [utmSourceFilter, setUtmSourceFilter] = useQueryState(
    'utm_source',
    parseAsString,
  );
  const [utmMediumFilter, setUtmMediumFilter] = useQueryState(
    'utm_medium',
    parseAsString,
  );
  const [utmContentFilter, setUtmContentFilter] = useQueryState(
    'utm_content',
    parseAsString,
  );
  const [utmTermFilter, setUtmTermFilter] = useQueryState(
    'utm_term',
    parseAsString,
  );
  const [offersFilter, setOffersFilter] = useQueryState(
    'offers',
    parseAsArrayOf(parseAsString),
  );
  const [productsFilter, setProductsFilter] = useQueryState(
    'products',
    parseAsArrayOf(parseAsString),
  );
  const [affiliatesFilter, setAffiliatesFilter] = useQueryState(
    'affiliates',
    parseAsArrayOf(parseAsString),
  );
  const [coproducersFilter, setCoproducersFilter] = useQueryState(
    'coproducers',
    parseAsArrayOf(parseAsString),
  );

  const currency = useCurrencyStore((s) => s.currency);
  const [debouncedQuery] = useDebounce(searchQuery, 650);

  const dateRange = useMemo(
    (): DateRange => ({
      from: fromDate,
      to: toDate,
    }),
    [fromDate, toDate],
  );

  const effectiveStatusFilter = statusFilter?.length
    ? statusFilter
    : DEFAULT_STATUS;

  const filterParams = useMemo((): FilterParams => {
    const params: FilterParams = { status: effectiveStatusFilter };
    if (paymentMethodsFilter?.length)
      params.payment_methods = paymentMethodsFilter;
    if (srcFilter) params.src = srcFilter;
    if (utmCampaignFilter) params.utm_campaign = utmCampaignFilter;
    if (utmSourceFilter) params.utm_source = utmSourceFilter;
    if (utmMediumFilter) params.utm_medium = utmMediumFilter;
    if (utmContentFilter) params.utm_content = utmContentFilter;
    if (utmTermFilter) params.utm_term = utmTermFilter;
    if (offersFilter?.length) params.offers = offersFilter;
    if (productsFilter?.length) params.products = productsFilter;
    if (affiliatesFilter?.length) params.affiliates = affiliatesFilter;
    if (coproducersFilter?.length) params.coproducers = coproducersFilter;
    return normalizeParams(params);
  }, [
    effectiveStatusFilter,
    paymentMethodsFilter,
    srcFilter,
    utmCampaignFilter,
    utmSourceFilter,
    utmMediumFilter,
    utmContentFilter,
    utmTermFilter,
    offersFilter,
    productsFilter,
    affiliatesFilter,
    coproducersFilter,
  ]);

  const filterVisualParams = useMemo(
    () => convertToVisualParams(filterParams),
    [filterParams],
  );

  const apiFilterParams = useMemo(
    () => convertToSubscriptionFilterParams(filterParams),
    [filterParams],
  );

  const resolvedFromDate = useMemo(() => {
    if (fromDate && isValidDate(fromDate)) {
      return fromDate;
    }
    return getDefaultDate();
  }, [fromDate]);

  const resolvedToDate = useMemo(() => {
    if (toDate && isValidDate(toDate)) {
      return toDate;
    }
    return getDefaultDate();
  }, [toDate]);

  const exportStartDate = useMemo(
    () => formatDateForAPI(resolvedFromDate),
    [resolvedFromDate],
  );

  const exportEndDate = useMemo(
    () => formatDateForAPI(resolvedToDate),
    [resolvedToDate],
  );

  const queryArgs = useMemo(
    () => ({
      startDate: formatDateForAPI(resolvedFromDate),
      endDate: formatDateForAPI(resolvedToDate),
      dateAxis: dateAxis as SubscriptionDateAxis,
      filterParams: apiFilterParams,
      q: debouncedQuery || undefined,
      currency,
    }),
    [
      resolvedFromDate,
      resolvedToDate,
      dateAxis,
      apiFilterParams,
      debouncedQuery,
      currency,
    ],
  );

  const { data: metricsData, isLoading: isLoadingMetrics } = useQuery({
    queryKey: ['subscription-metrics', queryArgs],
    queryFn: () => SubscriptionsService.getReportsByStatus(queryArgs),
    refetchOnWindowFocus: false,
  });

  const { data: chartRaw, isLoading: isLoadingChart } = useQuery({
    queryKey: ['subscription-chart', queryArgs],
    queryFn: () => SubscriptionsService.getTimeDistribution(queryArgs),
    refetchOnWindowFocus: false,
  });

  const chartData = useMemo(() => {
    const raw = chartRaw?.data ?? [];
    return raw.map((point: Record<string, unknown>) => ({
      hour: point.hour ?? point.date_formatted,
      active_count: point.active_count,
      active_count_formatted: point.active_count_formatted,
      recurring_revenue: point.recurring_revenue,
      recurring_revenue_formatted: point.recurring_revenue_formatted,
    }));
  }, [chartRaw]);

  const {
    data: subscriptions,
    isLoading: isLoadingSubscriptions,
    isFetching: isFetchingSubscriptions,
  } = useQuery({
    queryKey: ['subscriptions', queryArgs, currentPage],
    queryFn: () =>
      SubscriptionsService.getSubscriptions({
        ...queryArgs,
        page: currentPage,
      }),
    refetchOnWindowFocus: false,
  });

  const subscriptionsTableData = useMemo(
    () => ({
      data: subscriptions?.data ?? [],
      meta: { last_page: subscriptions?.meta?.last_page ?? 1 },
      links: subscriptions?.links,
    }),
    [subscriptions],
  );

  const getPeriodText = useCallback(
    (range: DateRange | undefined): string => {
      const locale = getLocale();
      if (!range?.from) return t('signatures:all_period');

      if (range.to && isSameDay(range.from, range.to) && isToday(range.from)) {
        return t('sales:single_day', {
          date: format(range.from, 'dd/MM/yyyy', { locale }),
        });
      }

      if (range.to && isSameDay(range.from, range.to)) {
        return t('sales:single_day', {
          date: format(range.from, 'dd/MM/yyyy', { locale }),
        });
      }

      if (range.to) {
        const days = differenceInDays(range.to, range.from);
        if (days === 6) return t('sales:last_7_days');
        if (days === 29) return t('sales:last_30_days');
        if (days === 89) return t('sales:last_90_days');

        return t('sales:date_range', {
          startDate: format(range.from, 'dd/MM', { locale }),
          endDate: format(range.to, 'dd/MM', { locale }),
        });
      }

      return t('signatures:all_period');
    },
    [t],
  );

  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      setCurrentPage(1);
      if (range?.from) {
        setFromDate(range.from);
      }
      if (range?.to) {
        setToDate(range.to);
      }
    },
    [setCurrentPage, setFromDate, setToDate],
  );

  const handleDateAxisChange = useCallback(
    (axis: SubscriptionDateAxis) => {
      setCurrentPage(1);
      setDateAxis(axis);
    },
    [setCurrentPage, setDateAxis],
  );

  const handleSearchChange = useCallback(
    (query: string) => {
      setCurrentPage(1);
      setSearchQuery(query || null);
    },
    [setCurrentPage, setSearchQuery],
  );

  const setFilterParams = useCallback(
    (params: FilterParams) => {
      setStatusFilter((params.status as string[]) || null);
      setPaymentMethodsFilter((params.payment_methods as string[]) || null);
      setSrcFilter((params.src as string) || null);
      setUtmCampaignFilter((params.utm_campaign as string) || null);
      setUtmSourceFilter((params.utm_source as string) || null);
      setUtmMediumFilter((params.utm_medium as string) || null);
      setUtmContentFilter((params.utm_content as string) || null);
      setUtmTermFilter((params.utm_term as string) || null);
      setOffersFilter((params.offers as string[]) || null);
      setProductsFilter((params.products as string[]) || null);
      setAffiliatesFilter((params.affiliates as string[]) || null);
      setCoproducersFilter((params.coproducers as string[]) || null);
    },
    [
      setStatusFilter,
      setPaymentMethodsFilter,
      setSrcFilter,
      setUtmCampaignFilter,
      setUtmSourceFilter,
      setUtmMediumFilter,
      setUtmContentFilter,
      setUtmTermFilter,
      setOffersFilter,
      setProductsFilter,
      setAffiliatesFilter,
      setCoproducersFilter,
    ],
  );

  const removeFilter = useCallback(
    (key: string) => {
      const map: Record<string, (v: null) => void> = {
        status: setStatusFilter,
        payment_methods: setPaymentMethodsFilter,
        src: setSrcFilter,
        utm_campaign: setUtmCampaignFilter,
        utm_source: setUtmSourceFilter,
        utm_medium: setUtmMediumFilter,
        utm_content: setUtmContentFilter,
        utm_term: setUtmTermFilter,
      };
      map[key]?.(null);
    },
    [
      setStatusFilter,
      setPaymentMethodsFilter,
      setSrcFilter,
      setUtmCampaignFilter,
      setUtmSourceFilter,
      setUtmMediumFilter,
      setUtmContentFilter,
      setUtmTermFilter,
    ],
  );

  const removeFilterItem = useCallback(
    (key: string, index: number) => {
      const current = filterParams[key] as string[] | undefined;
      if (!Array.isArray(current)) return;
      const next = current.filter((_, i) => i !== index);
      setFilterParams({ ...filterParams, [key]: next });
    },
    [filterParams, setFilterParams],
  );

  const clearAllFilters = useCallback(() => {
    setStatusFilter(null);
    setPaymentMethodsFilter(null);
    setSrcFilter(null);
    setUtmCampaignFilter(null);
    setUtmSourceFilter(null);
    setUtmMediumFilter(null);
    setUtmContentFilter(null);
    setUtmTermFilter(null);
    setOffersFilter(null);
    setProductsFilter(null);
    setAffiliatesFilter(null);
    setCoproducersFilter(null);
  }, [
    setStatusFilter,
    setPaymentMethodsFilter,
    setSrcFilter,
    setUtmCampaignFilter,
    setUtmSourceFilter,
    setUtmMediumFilter,
    setUtmContentFilter,
    setUtmTermFilter,
    setOffersFilter,
    setProductsFilter,
    setAffiliatesFilter,
    setCoproducersFilter,
  ]);

  const keyLabel: Record<string, string> = useMemo(
    () => ({
      status: t('sales:payment_status'),
      payment_methods: t('sales:payment_methods'),
      products: t('sales:products'),
      offers: t('sales:offer'),
      affiliates: t('sales:affiliate'),
      coproducers: t('sales:co_producer'),
      src: t('sales:src'),
      utm_source: t('sales:utm_source'),
      utm_medium: t('sales:utm_medium'),
      utm_campaign: t('sales:utm_campaign'),
      utm_content: t('sales:utm_content'),
      utm_term: t('sales:utm_term'),
    }),
    [t],
  );

  const filterBadgeLabel: Record<string, string> = useMemo(() => {
    const labels: Record<string, string> = {};
    SUBSCRIPTION_STATUSES.forEach((status) => {
      labels[status] = t(`signatures:status.${status}`);
    });
    return labels;
  }, [t]);

  const hasActiveFilters = useMemo(
    () => hasActiveFiltersBeyondDates(filterParams, fromDate, toDate),
    [filterParams, fromDate, toDate],
  );

  return {
    dateRange,
    dateAxis: dateAxis as SubscriptionDateAxis,
    handleDateAxisChange,
    handleDateRangeChange,
    handleSearchChange,
    getPeriodText,
    metrics: metricsData?.data,
    isLoadingMetrics,
    chartData,
    isLoadingChart,
    subscriptions: subscriptionsTableData,
    isLoadingSubscriptions,
    isFetchingSubscriptions,
    currentPage,
    setCurrentPage,
    searchQuery,
    setFilterParams,
    filterBadgeLabel,
    filterVisualParams,
    filterParams,
    hasActiveFiltersBeyondDates: hasActiveFilters,
    removeFilterItem,
    removeFilter,
    clearAllFilters,
    keyLabel,
    currency,
    exportStartDate,
    exportEndDate,
    exportFilterParams: apiFilterParams,
  };
}
